import { groupApi } from "../../api/groupApi"

import { addNotification, NotificationTypes, notifyException } from './notificationActions'
import { buildCacheKey, checkCache, setCache, invalidateCacheByPrefix } from '../../utils/actionCache'

const GROUP_LIST_TTL = 5 * 60 * 1000
const GROUP_LIST_CACHE_PREFIX = 'fetchGroupList'

import { businessEntityDictionarySignal, currentBusinessEntitySignal, businessEntitiesLoadedSignal } from '../signals/businessEntitySignals.js'

import { groupSignal, groupLoadedSignal, groupErrorSignal, groupListSignal, groupListLoadedSignal, groupListErrorSignal, groupDictionarySignal, groupRoleBusinessEntitiesSignal, groupRoleBusinessEntitiesLoadedSignal, groupRoleBusinessEntitiesErrorSignal, groupUserListSignal, groupUserListLoadedSignal, groupUserListErrorSignal, groupUserDictionarySignal, groupBusinessEntitiesSignal, groupBusinessEntitiesLoadedSignal, groupBusinessEntitiesErrorSignal, canGrantGroupListSignal, canGrantGroupListLoadedSignal, canGrantGroupListErrorSignal } from '../signals/groupSignals.js'

import { organizationRoleDictionarySignal } from '../signals/roleSignals.js'

export const insertGroup = async (businessEntityId, group, config, accessToken) => {
    invalidateCacheByPrefix(GROUP_LIST_CACHE_PREFIX)
    groupListLoadedSignal.value = false
    return await groupApi.insertGroup(businessEntityId, group, config, accessToken).then(
        async (group) => {
            groupListErrorSignal.value = null
            const parentId = businessEntitiesLoadedSignal.value ? currentBusinessEntitySignal.value.id : businessEntityId
            return await fetchGroupList(parentId, null, config, accessToken).then(
                () => {
                    return group
                }
            )
        }
    ).catch(
        (error) => {
            groupListErrorSignal.value = error
            notifyException(error, "Error Creating Group")
        }
    )
}

export const fetchGroup = async (groupId, config, accessToken) => {
    groupLoadedSignal.value = false
    groupSignal.value = null
    return await groupApi.fetchGroup(groupId, config, accessToken).then(
        (group) => {
            groupSignal.value = group
            groupLoadedSignal.value = true
            return group
        }
    ).catch(
        (error) => {
            groupErrorSignal.value = error
            notifyException(error.toString(), "Error fetching group")
        }
    )
}

export const updateGroup = async (id, group, config, accessToken) => {
    groupLoadedSignal.value = false
    if ("id" in group) {
        id = group.id
        delete group.id
    }
    return await groupApi.updateGroup(id, group, config, accessToken).then(
        (group) => {
            groupSignal.value = group
            groupLoadedSignal.value = true
            return group
        }
    ).catch(
        (error) => {
            groupErrorSignal.value = error
            throw error
        }
    )
}

export const addGroupUsers = async (groupId, userIds, config, accessToken) => {
    const f = "addGroupUsers()"
    //console.log(`${f}: adding group users`)
    groupUserListLoadedSignal.value = false
    const count = userIds.length
    return await groupApi.addGroupUsers(groupId, userIds, config, accessToken).then(
        async (users) => {
            //console.log(`${f}: inside then(); about to call fetchGroupUsers()`)
            if (count == 1) {
                addNotification(`Successfully added a user to this group`, NotificationTypes.SUCCESS, true, "Users Added to Group")
            } else {
                addNotification(`Successfully added ${count} users to this group`, NotificationTypes.SUCCESS, true, "Users Added to Group")
            }
            return await fetchGroupUserList(groupId, config, accessToken)
        }
    ).catch(
        (error) => {
            console.error(`${f}: inside catch()`)
            groupUserListErrorSignal.value = error
            notifyException(error, "Error adding users")
        }
    )
}

export const fetchGroupUserList = async (groupId, config, accessToken) => {
    const f = `fetchGroupUserList()`
    //console.log(`${f}: Fetching users for group ${groupId}`)
    groupUserListLoadedSignal.value = false
    groupUserListSignal.value = []
    groupUserDictionarySignal.value = {}
    return await groupApi.fetchGroupUserList(groupId, config, accessToken).then(
        (users) => {
            //console.log(`${f}: fetch complete`)
            groupUserListSignal.value = users
            // populate group user dictionary
            const dict = {}
            for (let i = 0; i < users.length; i++) {
                const user = users[i]
                dict[user.id] = user
            }
            groupUserDictionarySignal.value = dict
            groupUserListLoadedSignal.value = true
            return users
        }
    ).catch(
        (error) => {
            console.error(`${f}: fetch failed`)
            groupUserListErrorSignal.value = error
            notifyException(error.toString(), `Error fetching users for group ${groupId}`)
        }
    )
}

export const removeUserFromGroup = async (userId, groupId, config, accessToken) => {
    groupUserListLoadedSignal.value = false
    return await groupApi.removeUserFromGroup(userId, groupId, config, accessToken).then(
        async (response) => {
            return await fetchGroupUserList(groupId, config, accessToken).then(
                () => {
                    return response
                }
            )
        }
    ).catch(
        (error) => {
            groupUserListErrorSignal.value = error
            notifyException(error, `Error Removing User from Group`)
            throw error
        }
    )
}

export const addGroupRoleBusinessEntity = async (businessEntityId, groupId, entity, config, accessToken) => {
    groupRoleBusinessEntitiesLoadedSignal.value = false
    return await groupApi.addGroupRoleBusinessEntity(groupId, entity, config, accessToken).then(
        async (groupRoleBusinessEntity) => {
            addNotification("Successfully added new group", NotificationTypes.SUCCESS, "Success")
            return await fetchGroupRoleBusinessEntities(businessEntityId, groupId, config, accessToken).then(
                () => {
                    return groupRoleBusinessEntity
                }
            )
        }
    ).catch(
        (error) => {
            groupRoleBusinessEntitiesErrorSignal.value = error
            notifyException(error, "Error adding group role business entity")
        }
    )
}

export const fetchGroupBusinessEntities = async (businessEntityId, config, accessToken) => {
    groupBusinessEntitiesLoadedSignal.value = false
    groupBusinessEntitiesSignal.value = []
    return await groupApi.fetchGroupBusinessEntities(businessEntityId, config, accessToken).then(
        (groupBusinessEntities) => {
            groupBusinessEntities.value = groupBusinessEntities
            groupBusinessEntitiesErrorSignal.value = null
            groupBusinessEntitiesLoadedSignal.value = true
            return groupBusinessEntities
        }
    ).catch(
        (error) => {
            groupBusinessEntitiesErrorSignal.value = error
        }
    )
}

export const fetchGroupRoleBusinessEntities = async (businessEntityId, groupId, config, accessToken, assignRoles = true) => {
    let print = false
    groupRoleBusinessEntitiesLoadedSignal.value = false
    groupRoleBusinessEntitiesErrorSignal.value = null
    groupRoleBusinessEntitiesSignal.value = []
    return await groupApi.fetchGroupRoleBusinessEntities(businessEntityId, groupId, config, accessToken).then(
        (roleBusinessEntities) => {
            groupRoleBusinessEntitiesSignal.value = roleBusinessEntities
            if (assignRoles) {
                if (!organizationRoleDictionarySignal.value) {
                    throw Error("Organization role dictionary was not loaded")
                }
                for (let i = 0; i < roleBusinessEntities.length; i++) {
                    let rbe = roleBusinessEntities[i]
                    if (!("roleId" in rbe)) {
                        throw Error("Role business entity does not define a role ID")
                    }
                    const roleId = rbe.roleId
                    if (!(roleId in organizationRoleDictionarySignal.value)) {
                        throw Error(`Organization role dictionary does not know about a role with ID ${roleId}`)
                    }
                    const role = organizationRoleDictionarySignal.value[roleId]
                    rbe.role = role
                    if (!businessEntityDictionarySignal.value) {
                        console.error("Business entity dictionary does not have a value")
                        continue
                    }
                    //get list of business entities
                    let businessEntities = []
                    for (let i = 0; i < rbe.businessEntityIds.length; i++) {
                        const beid = rbe.businessEntityIds[i]
                        businessEntities.push(businessEntityDictionarySignal.value[beid])
                    }
                    rbe.businessEntities = businessEntities
                }
                if (print) {
                    console.log("Group role business entities:")
                    console.log(roleBusinessEntities)
                    console.log(organizationRoleDictionarySignal.value)
                }
            }
            groupRoleBusinessEntitiesLoadedSignal.value = true
            return roleBusinessEntities
        }
    ).catch(
        (error) => {
            groupRoleBusinessEntitiesErrorSignal.value = error
            notifyException(error.toString(), "Error loading role business entities")
        }
    )
}

export const deleteGroupRoleBusinessEntity = async (businessEntityId, groupId, roleId, config, accessToken) => {
    groupRoleBusinessEntitiesLoadedSignal.value = false
    return await groupApi.deleteGroupRoleBusinessEntity(groupId, roleId, config, accessToken).then(
        async () => {
            return await fetchGroupRoleBusinessEntities(businessEntityId, groupId, config, accessToken)
        }
    ).catch(
        (error) => {
            notifyException(error, "Deletion failed")
        }
    )
}

export const fetchGroupList = async (businessEntityId, filters, config, accessToken, forceRefresh=false) => {
    const cacheKey = buildCacheKey(GROUP_LIST_CACHE_PREFIX, businessEntityId)
    if (!forceRefresh) {
        const cached = checkCache(cacheKey, GROUP_LIST_TTL)
        if (cached) {
            return cached
        }
    }

    groupListLoadedSignal.value = false
    groupListErrorSignal.value = null
    groupListSignal.value = []
    return await groupApi.fetchGroupList(businessEntityId, config, accessToken).then(
        (rawGroups) => {
            let groups = []
            for (let i = 0; i < rawGroups.length; i++) {
                const newGroup = { ...rawGroups[i] }
                for (let j in Object.keys(rawGroups[i].properties)) {
                    const keyvalue = rawGroups[i].properties[j]
                    let value = parseInt(keyvalue.value)
                    switch (keyvalue.key) {
                        case 'Organizations':
                            newGroup.merchantCount = value
                            break
                        case 'Roles':
                            newGroup.roleCount = value
                            break
                        case 'Users':
                            newGroup.userCount = value
                            break
                    }
                }
                delete newGroup.properties
                groups.push(newGroup)
            }
            groupListSignal.value = groups
            groupListLoadedSignal.value = true
            setCache(cacheKey, groups)
            return groups
        }
    ).catch(
        (err) => {
            groupListErrorSignal.value = err.message
            throw err
        }
    )
}

export const fetchGroupDictionary = async (businessEntityId, filters, config, accessToken) => {
    return await fetchGroupList(businessEntityId, filters, config, accessToken).then(
        (groups) => {
            groupDictionarySignal.value = {}
            for (let i = 0; i < groups.length; i++) {
                const group = groups[i]
                groupDictionarySignal.value[group.id] = group
            }
            return groupDictionarySignal.value
        }
    )
}

export const deleteGroup = async (businessEntityId, groupId, config, accessToken) => {
    invalidateCacheByPrefix(GROUP_LIST_CACHE_PREFIX)
    groupListLoadedSignal.value = false
    return await groupApi.deleteGroup(groupId, config, accessToken).then(
        async (response) => {
            return await fetchGroupList(businessEntityId, null, config, accessToken).then(
                () => {
                    return response
                }
            )
        }
    ).catch(
        (error) => {
            groupListErrorSignal.value = error
            throw error
        }
    )
}

export const fetchCanGrantGroupList = async (businessEntityId, config, accessToken) => {
    canGrantGroupListLoadedSignal.value = false
    canGrantGroupListErrorSignal.value = null
    canGrantGroupListSignal.value = []
    return await groupApi.fetchCanGrantGroupList(businessEntityId, config, accessToken).then(
        (canGrantGroups) => {
            canGrantGroupListSignal.value = canGrantGroups
            canGrantGroupListLoadedSignal.value = true
            return canGrantGroups

        }
    ).catch(
        (err) => {
            canGrantGroupListErrorSignal.value = err.message
            throw err
        }
    )
}